import type { Holiday, ListHolidaysQuery } from '@clockwise/clients/holidays';
import { ApiError, countryName } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import { Download, Filter, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { DataTable } from '@/components/custom/data-table';
import { HolidayFormSheet } from '@/components/custom/holiday-form-sheet';
import { HolidayImportSheet } from '@/components/custom/holiday-import-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { holidaysApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['date', 'country', 'name', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

// Sentinels for the "no filter" options — Radix Select can't use an empty
// string as an item value.
const ALL_COUNTRIES = 'all';
const ALL_YEARS = 'all';

const ISO_DATE = 'yyyy-MM-dd';
const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'date';

// A calendar defaults to the year you're in — that's the window a user
// actually wants when they open Setup → Holidays. The picker spans a couple of
// years back (for reference) and a few forward (so next year can be loaded
// ahead of time). Both are read at RENDER time, never at module load: a
// long-lived SPA tab open across New Year's Eve would otherwise keep defaulting
// to last year until a hard reload.
function currentYear(): number {
  return new Date().getFullYear();
}
function yearOptions(year: number): number[] {
  return Array.from({ length: 6 }, (_, i) => year - 2 + i);
}

/** Query params arrive as strings; an empty one means "absent", not a value.
 *  Without this, `?year=` coerces via `Number('') === 0` (the trap named in
 *  CONTRIBUTING G15), fails `min(1970)`, fails the `'all'` literal too, and
 *  throws out of `validateSearch` — crashing the route instead of falling back
 *  to the default. Same for the fixed-length `country`. */
const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

const listSearch = z.object({
  q: z.string().optional(),
  page: z.preprocess(blankToUndefined, z.coerce.number().int().min(0).optional()),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  country: z.preprocess(blankToUndefined, z.string().length(2).optional()),
  // `all` keeps "every year" bookmarkable; absent means the default (this year).
  year: z.preprocess(
    blankToUndefined,
    z.union([z.coerce.number().int().min(1970).max(2999), z.literal('all')]).optional(),
  ),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/holidays')({
  validateSearch: listSearch,
  component: HolidaysPage,
});

function HolidaysPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const thisYear = currentYear();
  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const countryFilter = search.country;
  const yearFilter: number | 'all' = search.year ?? thisYear;

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }, thisYear),
      replace: true,
    });

  const [editing, setEditing] = useState<Holiday | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  // The year picker is a convenience over the API's general inclusive window.
  // NOT named `window` — that would shadow the browser global for the whole
  // component body, so any later `window.*` call would resolve to this object.
  const dateWindow = yearWindow(yearFilter);

  const listQuery: ListHolidaysQuery = {
    q: q.trim() || undefined,
    country: countryFilter,
    from: dateWindow?.from,
    to: dateWindow?.to,
    // The calendar always shows live holidays. Deleting is soft, so a removed
    // row is still on record for audit and for historical working-day
    // calculations — it just stops appearing here. Restoring one is an API
    // operation (POST /api/holidays/:id/restore); there is no UI path back.
    active: true,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.holidays.list(listQuery),
    queryFn: () => holidaysApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  // Only the countries this tenant actually has holidays for — offering all
  // 249 ISO codes here would be noise.
  const countriesQuery = useQuery({
    queryKey: queryKeys.holidays.countries(),
    queryFn: () => holidaysApi.countries(),
  });
  const availableCountries = countriesQuery.data?.data ?? [];

  // A bookmarked ?country=XX can outlive the rows it pointed at (all deleted,
  // or a link from another tenant). Once the list has loaded, drop a filter
  // that resolves to nothing — otherwise the dropdown reads "All countries"
  // while the list stays filtered to an invisible one.
  useEffect(() => {
    if (countriesQuery.isSuccess && countryFilter && !availableCountries.includes(countryFilter)) {
      updateSearch({ country: undefined, page: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesQuery.isSuccess, countryFilter, availableCountries]);

  const exportMutation = useMutation({
    mutationFn: () =>
      holidaysApi.exportXlsx({
        // Mirrors `listQuery` exactly, free-text search included — the file
        // must contain the rows on screen, not a wider set.
        q: q.trim() || undefined,
        country: countryFilter,
        from: dateWindow?.from,
        to: dateWindow?.to,
        active: true,
      }),
    onSuccess: ({ blob, filename }) => triggerDownload(blob, filename ?? 'holidays.xlsx'),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Export failed');
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => holidaysApi.remove(id),
    onSuccess: () => {
      toast.success('Holiday deleted');
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed');
    },
  });

  const openEditor = (holiday: Holiday | null) => {
    setEditing(holiday);
    setSheetOpen(true);
  };

  const columns = useMemo<ColumnDef<Holiday>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        size: 130,
        cell: ({ row }) => (
          <span className="font-medium whitespace-nowrap">{formatIsoDate(row.original.date)}</span>
        ),
      },
      {
        accessorKey: 'country',
        header: 'Country',
        size: 150,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5">
            <span className="font-mono text-xs text-muted-foreground">{row.original.country}</span>
            <span className="break-words">{countryName(row.original.country)}</span>
          </span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Holiday',
        size: 200,
        cell: ({ row }) => (
          <span className="block break-words" title={row.original.name}>
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 220,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.description ? (
            <span className="block break-words">{row.original.description}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        size: 110,
        cell: ({ row }) => (
          <span className="text-xs whitespace-nowrap text-muted-foreground">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 72,
        enableSorting: false,
        cell: ({ row }) => (
          <RequirePermission permission="holiday.edit">
            <RowActions>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditor(row.original);
                }}
              >
                <Pencil />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Delete"
                disabled={deleteMutation.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    confirm(
                      `Delete "${row.original.name}"? It will stop counting as a non-working day and leave this calendar.`,
                    )
                  ) {
                    deleteMutation.mutate(row.original.id);
                  }
                }}
              >
                <Trash2 />
              </Button>
            </RowActions>
          </RequirePermission>
        ),
      },
    ],
    [deleteMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));
  const filtered = Boolean(q) || Boolean(countryFilter) || yearFilter !== 'all';

  return (
    <PageShell>
      <PageHeader
        title="Holidays"
        subtitle="Non-working dates per country. Used to work out working days in a month."
        count={total}
      >
        <RequirePermission permission="holiday.view">
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </RequirePermission>
        <RequirePermission permission="holiday.edit">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import
          </Button>
        </RequirePermission>
        <RequirePermission permission="holiday.edit">
          <Button onClick={() => openEditor(null)}>
            <Plus />
            Add holiday
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search holiday, description…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={countryFilter ?? ALL_COUNTRIES}
          onValueChange={(v) =>
            updateSearch({ country: v === ALL_COUNTRIES ? undefined : v, page: 0 })
          }
        >
          <SelectTrigger className="w-[200px]" aria-label="Country">
            <SelectValue placeholder="All countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_COUNTRIES}>All countries</SelectItem>
            {availableCountries.map((code) => (
              <SelectItem key={code} value={code}>
                {countryName(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={String(yearFilter)}
          onValueChange={(v) =>
            updateSearch({ year: v === ALL_YEARS ? 'all' : Number(v), page: 0 })
          }
        >
          <SelectTrigger className="w-[130px]" aria-label="Year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_YEARS}>All years</SelectItem>
            {yearOptions(thisYear).map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {countriesQuery.isError ? (
        <p className="rounded-md border border-destructive/40 px-3 py-2 text-xs text-destructive">
          Could not load the country filter
          {countriesQuery.error instanceof ApiError ? ` — ${countriesQuery.error.message}` : '.'}{' '}
          The list below is unfiltered by country.
        </p>
      ) : null}

      <DataTable
        table={table}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Failed to load holidays.'
        }
        emptyMessage={
          filtered
            ? 'No holidays match these filters.'
            : 'No holidays yet. Click “Add holiday”, or import a year at once.'
        }
        onRowClick={openEditor}
      />

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <HolidayFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        holiday={editing}
      />
      <HolidayImportSheet open={importOpen} onOpenChange={setImportOpen} />
    </PageShell>
  );
}

/** Inclusive `[Jan 1, Dec 31]` bounds for a year, or `undefined` for "all". */
function yearWindow(year: number | 'all'): { from: string; to: string } | undefined {
  if (year === 'all') return undefined;
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/**
 * `2026-12-25` → `Dec 25, 2026`. Parsed explicitly in local time: passing the
 * string to `new Date()` parses it as UTC midnight, which renders as the
 * PREVIOUS day for anyone west of Greenwich.
 */
function formatIsoDate(value: string): string {
  return format(parse(value, ISO_DATE, new Date()), 'PP');
}

function stripDefaults(s: ListSearch, thisYear: number): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  if (s.country) out.country = s.country;
  // The default (this year) stays out of the URL; every other choice —
  // including 'all' — is bookmarkable.
  if (s.year !== undefined && s.year !== thisYear) out.year = s.year;
  return out;
}
